import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReceiptIcon, PrinterIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const Row = ({ label, value, strong, testId }) => (
  <div className={`flex justify-between ${strong ? "font-semibold text-base border-t border-border pt-2 mt-1" : ""}`}>
    <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
    <span className="font-mono-plex" data-testid={testId}>{value}</span>
  </div>
);

function ProformaBody({ d }) {
  const lines = d.line_items || [];
  return (
    <div className="space-y-4 text-sm" data-testid="proforma-body">
      <div className="rounded-md bg-muted/60 p-3 text-xs grid grid-cols-2 gap-1">
        <span>Guest: <b>{d.guest?.name}</b></span>
        <span>Room: <b data-testid="proforma-room">{d.room?.number}</b> · {d.room?.room_type}</span>
        <span>{d.reservation?.check_in} → {d.reservation?.check_out}</span>
        <span>{d.invoice ? <>Invoice <b>{d.invoice.number}</b> ({d.invoice.status})</> : "Proforma (not yet invoiced)"}</span>
      </div>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-1">Description</th><th className="py-1 text-right">Nights / Qty</th><th className="py-1 text-right">Rate</th><th className="py-1 text-right">Amount</th></tr></thead>
        <tbody>
          <tr className="border-b border-border/60" data-testid="proforma-room-line">
            <td className="py-1.5">Room {d.room?.number} · {d.room?.room_type}</td>
            <td className="py-1.5 text-right">{d.nights} Night{d.nights !== 1 ? "s" : ""}</td>
            <td className="py-1.5 text-right font-mono-plex">{inr(d.rate_per_night)}</td>
            <td className="py-1.5 text-right font-mono-plex">{inr(d.room_charges)}</td>
          </tr>
          {lines.map((li, i) => li.is_header ? (
            <tr key={i}><td colSpan={4} className="pt-2 pb-0.5 font-medium text-primary">{li.description}</td></tr>
          ) : (
            <tr key={i} className="border-b border-border/40" data-testid={`proforma-line-${i}`}>
              <td className="py-1">{li.description}</td>
              <td className="py-1 text-right">{li.qty}</td>
              <td className="py-1 text-right font-mono-plex">{inr(li.rate)}</td>
              <td className="py-1 text-right font-mono-plex">{inr(li.amount)}</td>
            </tr>
          ))}
          {d.extra_charges > 0 && <tr><td className="py-1">Extra charges</td><td /><td /><td className="py-1 text-right font-mono-plex">{inr(d.extra_charges)}</td></tr>}
        </tbody>
      </table>
      {!d.pos_orders?.length && <p className="text-xs text-muted-foreground italic" data-testid="proforma-no-pos">No food / POS bills posted to this room yet.</p>}
      <div className="ml-auto w-64 space-y-1">
        {d.discount > 0 && <Row label="Discount" value={`– ${inr(d.discount)}`} />}
        <Row label={`GST ${d.tax_inclusive ? "(included)" : ""}`} value={inr(d.tax_amount)} testId="proforma-tax" />
        <Row label="Total" value={inr(d.total)} testId="proforma-total" />
        <Row label="Advance / paid" value={`– ${inr(Number(d.deposit_applied || 0) + Number(d.paid || 0))}`} testId="proforma-paid" />
        <Row label="Balance due" value={inr(d.balance_due)} strong testId="proforma-balance" />
      </div>
    </div>
  );
}

export const ProformaButton = ({ reservationId, testIdPrefix = "proforma" }) => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!open) return;
    setD(null);
    api.get(`/reservations/${reservationId}/proforma`).then((r) => setD(r.data)).catch(() => setOpen(false));
  }, [open, reservationId]);
  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`${testIdPrefix}-btn`}>
        <ReceiptIcon size={14} className="mr-1" /> View bill
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testIdPrefix}-dialog`}>
          <DialogHeader><DialogTitle>Running bill (proforma)</DialogTitle></DialogHeader>
          {d ? <ProformaBody d={d} /> : <p className="p-4 text-sm">Loading…</p>}
          {d?.invoice && (
            <Button variant="outline" size="sm" className="w-fit" onClick={() => window.open(`/invoices/${d.invoice.id}/print`, "_blank")} data-testid={`${testIdPrefix}-open-invoice`}>
              <PrinterIcon size={14} className="mr-1" /> Open invoice {d.invoice.number}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
